/**
 * Platform SSO client — typed wrapper over the shared auth surface
 * (@blazorly/auth-contract) hosted by the blazorly-agent worker.
 *
 * All calls carry credentials so the Domain=.blazorly.com refresh cookie
 * (host-only `blazorly-refresh` on localhost) round-trips cross-origin.
 * The access token is kept in localStorage under the legacy `auth_token`
 * key so the existing API client (`lib/api.ts`) keeps working unchanged.
 */

import { AuthClientError, createAuthClient, type AuthClient } from '@blazorly/auth-contract';
import { config } from '../config';

export const TOKEN_KEY = 'auth_token';
export const SESSION_KEY = 'session_id';

let client: AuthClient | null = null;

export function getAuthClient(): AuthClient {
	if (!client) {
		client = createAuthClient({
			baseUrl: config.authApiUrl,
			pathPrefix: '/auth',
		});
	}
	return client;
}

export function getStoredToken(): string | null {
	return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
	localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(SESSION_KEY);
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Silent refresh via the shared cookie. Returns a fresh access token or
 * null when no valid session cookie exists. Concurrent callers share one
 * in-flight request.
 */
export function isUnauthorizedError(error: unknown): boolean {
	return error instanceof AuthClientError && error.status === 401;
}

export function refreshAccessToken(): Promise<string | null> {
	if (refreshInFlight) return refreshInFlight;
	refreshInFlight = (async () => {
		try {
			const refreshed = await getAuthClient().refresh();
			setStoredToken(refreshed.token);
			return refreshed.token;
		} catch (error) {
			if (isUnauthorizedError(error)) clearStoredToken();
			return null;
		}
	})().finally(() => {
		refreshInFlight = null;
	});
	return refreshInFlight;
}

const SESSION_CHANNEL = 'blazorly-auth-session';

export function notifySessionEnded(): void {
	try {
		new BroadcastChannel(SESSION_CHANNEL).postMessage({ type: 'logout' });
	} catch {
		// BroadcastChannel is unavailable in some private contexts.
	}
}

export function onSessionEnded(handler: () => void): () => void {
	try {
		const channel = new BroadcastChannel(SESSION_CHANNEL);
		channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
			if (event.data?.type === 'logout') handler();
		};
		return () => channel.close();
	} catch {
		return () => undefined;
	}
}
